import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button, Card, CardContent, LinearProgress,
  Divider, CircularProgress, Stack, Chip, Paper, Slider, IconButton,
  alpha,
} from "@mui/material";
import TopBar from "../globalComponents/topbar";
import Footer from "../globalComponents/footer";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  CheckCircle, CalendarToday, Notes, ImageNotSupported,
  ArrowBack, Flag, Edit, RadioButtonUnchecked, TimelapseOutlined,
  WarningAmber, EmojiEvents, TrackChanges, RestartAlt,
} from "@mui/icons-material";

// ── Brand palette ────────────────────────────────────────────────────────────
const C = {
  primary: "#0A2E8A",
  primaryDark: "#061E5C",
  primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC",
  primarySofter: "#F2F5FE",
  secondary: "#10682A",
  secondaryDark: "#0A4A1C",
  secondaryLight: "#2E9148",
  secondarySoft: "#E3F2E8",
  secondarySofter: "#F0F8F3",
  bg: "#FAFBFC",
  paper: "#FFFFFF",
  border: "#E5E9F0",
  borderSoft: "#EEF1F6",
  textPrimary: "#0D1117",
  textSecondary: "#5B6B7E",
  textMuted: "#8A9BB0",
  warning: "#D97706",
  warningSoft: "#FEF3C7",
  error: "#DC2626",
  errorSoft: "#FEE2E2",
};

const API_URL = "https://backend-global-1vq9.onrender.com/api/goals";

const PRIORITY_COLORS = {
  High:   { bg: C.errorSoft,     color: C.error,     border: alpha(C.error, 0.25) },
  Medium: { bg: C.warningSoft,   color: C.warning,   border: alpha(C.warning, 0.25) },
  Low:    { bg: C.secondarySoft, color: C.secondary, border: alpha(C.secondary, 0.25) },
};

const STATUS_CONFIG = {
  not_started: { label: "Not started", color: C.textMuted,  bg: C.borderSoft,    icon: <RadioButtonUnchecked sx={{ fontSize: 14 }} /> },
  in_progress: { label: "In progress", color: C.primary,    bg: C.primarySoft,   icon: <TimelapseOutlined sx={{ fontSize: 14 }} /> },
  completed:   { label: "Completed",   color: C.secondary,  bg: C.secondarySoft, icon: <CheckCircle sx={{ fontSize: 14 }} /> },
  overdue:     { label: "Overdue",     color: C.error,      bg: C.errorSoft,     icon: <WarningAmber sx={{ fontSize: 14 }} /> },
};

const computeStatus = (goal) => {
  const progress = Number(goal?.progress) || 0;
  const explicit = goal?.status;
  if (explicit === "completed" || progress >= 100) return "completed";
  if (goal?.dueDate) {
    const due = new Date(goal.dueDate);
    if (!isNaN(due.getTime()) && due.getTime() < Date.now()) return "overdue";
  }
  if (explicit === "in_progress" || progress > 0) return "in_progress";
  return "not_started";
};

// Resolve picture URL — accepts absolute URLs, relative paths, and OS-specific separators
const resolveImageUrl = (picture) => {
  if (!picture) return null;
  if (/^https?:\/\//i.test(picture)) return picture;
  const apiBase = API_URL.replace(/\/api\/goals$/, "");
  // Normalize Windows backslashes that multer sometimes produces
  return `${apiBase}/${picture.replace(/\\/g, "/")}`;
};

export default function GoalDetails() {
  const { goalId } = useParams();
  const navigate = useNavigate();
  const [goal, setGoal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/${goalId}`);
        setGoal(res.data);
        setProgress(Number(res.data?.progress) || 0);
      } catch (err) {
        console.error("Error fetching goal details:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [goalId]);

  // Persist progress + derived status to backend
  const saveProgress = async (newProgress) => {
    setSaving(true);
    const newStatus = newProgress >= 100 ? "completed" : newProgress > 0 ? "in_progress" : "not_started";
    // Optimistic update
    setProgress(newProgress);
    setGoal((g) => g ? { ...g, progress: newProgress, status: newStatus } : g);
    try {
      const formData = new FormData();
      formData.append("progress", newProgress);
      formData.append("status", newStatus);
      const res = await axios.put(`${API_URL}/${goalId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Trust the server's returned goal
      const updated = res.data?.goal || res.data;
      if (updated && updated._id) setGoal(updated);
    } catch (err) {
      console.error("Error saving progress:", err);
      // Roll back on failure
      try {
        const res = await axios.get(`${API_URL}/${goalId}`);
        setGoal(res.data);
        setProgress(Number(res.data?.progress) || 0);
      } catch {}
    } finally {
      setSaving(false);
    }
  };

  const markCompleted = () => saveProgress(100);
  const resetProgress = () => saveProgress(0);

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: C.bg }}>
        <TopBar />
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <CircularProgress size={48} thickness={5} sx={{ color: C.primary }} />
        </Box>
        <Footer />
      </Box>
    );
  }

  // Not found
  if (!goal) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: C.bg }}>
        <TopBar />
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" flex={1} gap={2}>
          <Flag sx={{ fontSize: 56, color: C.textMuted, opacity: 0.5 }} />
          <Typography fontWeight={600} color="text.secondary">Goal not found.</Typography>
          <Button
            variant="outlined" onClick={() => navigate(-1)}
            sx={{
              borderRadius: 2.5, textTransform: "none",
              borderColor: C.primary, color: C.primary,
              "&:hover": { bgcolor: C.primarySoft, borderColor: C.primaryDark },
            }}
          >
            ← Go Back
          </Button>
        </Box>
        <Footer />
      </Box>
    );
  }

  const p = PRIORITY_COLORS[goal.priority] || PRIORITY_COLORS.Medium;
  const status = computeStatus({ ...goal, progress });
  const sc = STATUS_CONFIG[status];
  const isCompleted = status === "completed";
  const isOverdue = status === "overdue";
  const imageUrl = resolveImageUrl(goal.picture);

  // Hero accent based on status
  const heroGradient = isCompleted
    ? `linear-gradient(135deg, ${C.secondary} 0%, ${C.secondaryDark} 100%)`
    : isOverdue
      ? `linear-gradient(135deg, ${C.error} 0%, #991B1B 100%)`
      : `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`;

  const heroOrb = isCompleted
    ? `radial-gradient(circle, ${C.secondaryLight}50 0%, transparent 70%)`
    : `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: C.bg }}>
      <TopBar />

      <Box sx={{ flex: 1, px: { xs: 2, sm: 3, md: 6, lg: 16 }, py: { xs: 3, md: 5 } }}>

        {/* Back row */}
        <Stack direction="row" alignItems="center" spacing={1} mb={2.5}>
          <Button
            startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
            onClick={() => navigate(-1)}
            sx={{
              textTransform: "none", fontWeight: 600, fontSize: 13,
              color: C.textSecondary, borderRadius: 2,
              "&:hover": { bgcolor: C.borderSoft },
            }}
          >
            Back to Goals
          </Button>
        </Stack>

        <Box sx={{ maxWidth: 760, mx: "auto" }}>

          {/* ── Hero card ────────────────────────────────────────── */}
          <Paper
            elevation={0}
            sx={{
              position: "relative",
              borderRadius: 3, overflow: "hidden",
              background: heroGradient,
              p: { xs: 3, sm: 4 }, mb: 2.5,
              color: "#fff",
              boxShadow: isCompleted
                ? `0 8px 30px ${C.secondary}30`
                : isOverdue
                  ? `0 8px 30px ${C.error}30`
                  : `0 8px 30px ${C.primary}30`,
            }}
          >
            <Box sx={{
              position: "absolute",
              top: "-40%", right: "-10%",
              width: { xs: 200, sm: 280 }, height: { xs: 200, sm: 280 },
              borderRadius: "50%",
              background: heroOrb,
              pointerEvents: "none",
            }} />

            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1.5}>
                <Box sx={{
                  width: 56, height: 56,
                  borderRadius: 2,
                  bgcolor: "rgba(255,255,255,0.18)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                  backdropFilter: "blur(6px)",
                }}>
                  {isCompleted ? <EmojiEvents sx={{ fontSize: 30, color: "#fff" }} /> : <TrackChanges sx={{ fontSize: 30, color: "#fff" }} />}
                </Box>

                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  <Chip
                    icon={React.cloneElement(sc.icon, { sx: { fontSize: 14, color: "#fff !important" } })}
                    label={sc.label}
                    size="small"
                    sx={{
                      height: 24, fontSize: 11, fontWeight: 700,
                      bgcolor: "rgba(255,255,255,0.22)", color: "#fff",
                      backdropFilter: "blur(4px)",
                      "& .MuiChip-icon": { color: "#fff" },
                    }}
                  />
                  <Chip
                    icon={<Flag sx={{ fontSize: "13px !important", color: "#fff !important" }} />}
                    label={goal.priority}
                    size="small"
                    sx={{
                      height: 24, fontSize: 11, fontWeight: 700,
                      bgcolor: "rgba(255,255,255,0.22)", color: "#fff",
                      "& .MuiChip-icon": { color: "#fff" },
                    }}
                  />
                </Stack>
              </Stack>

              <Typography sx={{
                fontWeight: 800, fontSize: { xs: 22, sm: 28, md: 32 },
                color: "#fff", lineHeight: 1.2,
                letterSpacing: -0.5,
                textDecoration: isCompleted ? "line-through" : "none",
                textDecorationColor: "rgba(255,255,255,0.5)",
                mb: 1.5,
              }}>
                {goal.title}
              </Typography>

              {goal.dueDate && (
                <Stack direction="row" spacing={0.75} alignItems="center" mb={2}>
                  <CalendarToday sx={{ fontSize: 14, color: "rgba(255,255,255,0.8)" }} />
                  <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.85)", fontWeight: 600 }}>
                    Due {new Date(goal.dueDate).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" })}
                  </Typography>
                </Stack>
              )}

              {/* Progress bar in hero */}
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
                  <Typography sx={{
                    fontSize: 11, fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: 0.8,
                    color: "rgba(255,255,255,0.85)",
                  }}>
                    Progress
                  </Typography>
                  <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#fff" }}>
                    {progress}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 10, borderRadius: 5,
                    bgcolor: "rgba(255,255,255,0.18)",
                    "& .MuiLinearProgress-bar": {
                      background: "linear-gradient(90deg, #fff, rgba(255,255,255,0.85))",
                      borderRadius: 5,
                    },
                  }}
                />
              </Box>
            </Box>
          </Paper>

          {/* ── Update progress card ─────────────────────────────── */}
          <Card
            elevation={0}
            sx={{
              position: "relative",
              borderRadius: 3,
              border: `1px solid ${C.border}`,
              bgcolor: C.paper,
              mb: 2.5,
              overflow: "hidden",
              boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0, left: 0, right: 0,
                height: "3px",
                background: `linear-gradient(90deg, ${C.primary}, ${C.primary}88)`,
              },
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Stack direction="row" alignItems="center" spacing={1.25} mb={2}>
                <TrackChanges sx={{ fontSize: 18, color: C.primary }} />
                <Typography fontWeight={700} fontSize={14} color={C.textPrimary}>
                  Update Progress
                </Typography>
              </Stack>

              <Box px={1.5} mb={2.5}>
                <Slider
                  value={progress}
                  onChange={(_, v) => setProgress(v)}
                  onChangeCommitted={(_, v) => saveProgress(v)}
                  disabled={saving}
                  min={0} max={100} step={5}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${v}%`}
                  marks={[
                    { value: 0, label: "0%" },
                    { value: 25, label: "25%" },
                    { value: 50, label: "50%" },
                    { value: 75, label: "75%" },
                    { value: 100, label: "100%" },
                  ]}
                  sx={{
                    color: isCompleted ? C.secondary : C.primary,
                    "& .MuiSlider-thumb": {
                      width: 20, height: 20,
                      boxShadow: `0 2px 8px ${(isCompleted ? C.secondary : C.primary)}50`,
                      "&:hover, &.Mui-focusVisible": {
                        boxShadow: `0 0 0 8px ${alpha(isCompleted ? C.secondary : C.primary, 0.16)}`,
                      },
                    },
                    "& .MuiSlider-rail": { bgcolor: C.borderSoft, opacity: 1, height: 6 },
                    "& .MuiSlider-track": {
                      height: 6, border: "none",
                      background: isCompleted
                        ? `linear-gradient(90deg, ${C.secondary}, ${C.secondaryLight})`
                        : `linear-gradient(90deg, ${C.primary}, ${C.primaryLight})`,
                    },
                    "& .MuiSlider-mark": { bgcolor: C.textMuted, width: 2, height: 6 },
                    "& .MuiSlider-markLabel": { fontSize: 10, color: C.textMuted, fontWeight: 600 },
                    "& .MuiSlider-valueLabel": {
                      bgcolor: isCompleted ? C.secondary : C.primary,
                      borderRadius: 1.5,
                      fontSize: 11, fontWeight: 700,
                    },
                  }}
                />
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<RestartAlt sx={{ fontSize: 17 }} />}
                  onClick={resetProgress}
                  disabled={saving || progress === 0}
                  sx={{
                    py: 1.25, borderRadius: 2.5, textTransform: "none",
                    fontWeight: 700, fontSize: 13,
                    borderColor: C.border, color: C.textSecondary,
                    "&:hover": { borderColor: C.textSecondary, bgcolor: C.borderSoft },
                  }}
                >
                  Reset
                </Button>

                <Button
                  fullWidth
                  variant="contained"
                  disabled={saving || isCompleted}
                  startIcon={<CheckCircle sx={{ fontSize: 17 }} />}
                  onClick={markCompleted}
                  sx={{
                    py: 1.25, borderRadius: 2.5, textTransform: "none",
                    fontWeight: 700, fontSize: 13, boxShadow: "none",
                    background: isCompleted
                      ? `linear-gradient(135deg, ${C.secondary}, ${C.secondaryDark})`
                      : `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
                    "&:hover": {
                      boxShadow: `0 4px 16px ${(isCompleted ? C.secondary : C.primary)}40`,
                    },
                    "&.Mui-disabled": {
                      background: `linear-gradient(135deg, ${C.secondary}, ${C.secondaryDark})`,
                      color: "#fff", opacity: 1,
                    },
                  }}
                >
                  {isCompleted ? "✓ Completed" : "Mark as Completed"}
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* ── Notes card ───────────────────────────────────────── */}
          <Card
            elevation={0}
            sx={{
              position: "relative",
              borderRadius: 3,
              border: `1px solid ${C.border}`,
              bgcolor: C.paper,
              mb: 2.5,
              overflow: "hidden",
              boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0, left: 0, right: 0,
                height: "3px",
                background: `linear-gradient(90deg, ${C.secondary}, ${C.secondary}88)`,
              },
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                <Stack direction="row" alignItems="center" spacing={1.25}>
                  <Notes sx={{ fontSize: 18, color: C.secondary }} />
                  <Typography fontWeight={700} fontSize={14} color={C.textPrimary}>
                    My Notes
                  </Typography>
                </Stack>
                <IconButton
                  size="small"
                  onClick={() => navigate("/mygoal")}
                  sx={{ color: C.primary, "&:hover": { bgcolor: C.primarySoft } }}
                >
                  <Edit sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>

              {goal.note?.trim() ? (
                <Box sx={{
                  bgcolor: C.secondarySofter,
                  borderRadius: 2.5,
                  p: { xs: 2, sm: 2.5 },
                  border: `1px solid ${alpha(C.secondary, 0.18)}`,
                }}>
                  <Typography sx={{
                    fontSize: 13.5, color: C.textPrimary,
                    lineHeight: 1.65,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}>
                    {goal.note}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{
                  py: 3,
                  textAlign: "center",
                  bgcolor: C.borderSoft,
                  borderRadius: 2.5,
                }}>
                  <Typography sx={{ fontSize: 13, color: C.textMuted, fontStyle: "italic" }}>
                    No notes for this goal yet. Tap edit to add one.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* ── Image card ───────────────────────────────────────── */}
          {imageUrl ? (
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${C.border}`,
                bgcolor: C.paper,
                overflow: "hidden",
                boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
              }}
            >
              <Box
                component="img"
                src={imageUrl}
                alt={goal.title}
                onError={(e) => { e.target.style.display = "none"; }}
                sx={{
                  width: "100%",
                  maxHeight: 420,
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </Card>
          ) : (
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px dashed ${C.border}`,
                bgcolor: C.bg,
                py: 4,
                textAlign: "center",
              }}
            >
              <ImageNotSupported sx={{ fontSize: 28, color: C.textMuted, opacity: 0.6, mb: 1 }} />
              <Typography sx={{ fontSize: 13, color: C.textMuted, fontStyle: "italic" }}>
                No image attached to this goal
              </Typography>
            </Card>
          )}
        </Box>
      </Box>

      <Footer />
    </Box>
  );
}